import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

const BusyContext = createContext(false);          // önizleme beklenirken karar girdisi kapalı (fare + klavye)
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, Check, Info, Loader2, Wrench } from "lucide-react";
import { classifyIssue, money, quantity, PREVIEW_ERROR_TEXT } from "./quickImport/model";
import { useQuickImportPreview } from "./quickImport/useQuickImportPreview";
export { classifyIssue };

// Üç adımlı, SALT OKUNUR içeri alma prototipi. Yalnız /quick-import/preview çağırır; confirm, katalog ve stok yazımı bu bileşende YOK.
// Sunucu kodları arayüze çıkmaz: her kod üç sınıftan birine düşer (kullanıcı düzeltir / belge kaynaklı / sistem). Bilinmeyen kod = sistem.

const STEPS = ["Faturayı kontrol et", "Kalemleri düzenle", "Sonucu gözden geçir"];


function lineLabel(line) {
  return line == null ? "" : `Satır ${line + 1}: `;
}

// WAI-ARIA radyo grubu: tek sekme durağı (seçili ya da ilk), ok tuşları seçer ve odağı taşır.
function Segmented({ value, options, onChange, name }) {
  const busy = useContext(BusyContext);
  const refs = useRef([]);
  const current = options.findIndex(([v]) => v === value);
  const onKeyDown = (event) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (!step) return;
    event.preventDefault();
    if (busy) return;
    const next = ((current < 0 ? 0 : current) + step + options.length) % options.length;
    refs.current[next]?.focus();
    if (options[next][0] !== value) onChange(options[next][0]);
  };
  return (
    <div role="radiogroup" aria-label={name} className="inline-flex rounded-lg border border-border p-0.5" onKeyDown={onKeyDown}>
      {options.map(([v, text], i) => (
        <button key={v} ref={(el) => { refs.current[i] = el; }} type="button" role="radio" aria-checked={value === v} aria-disabled={busy} tabIndex={i === (current < 0 ? 0 : current) ? 0 : -1}
          onClick={() => { if (!busy) onChange(v); }}
          className={`rounded-md px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${value === v ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted"}`}>{text}</button>
      ))}
    </div>
  );
}

function StepHeader({ step, onStep, counts }) {
  return (
    <ol className="grid grid-cols-3 gap-2" data-testid="wizard-steps">
      {STEPS.map((title, i) => (
        <li key={title}>
          <button type="button" onClick={() => onStep(i)} aria-current={step === i ? "step" : undefined}
            className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm ${step === i ? "border-primary bg-primary/5 font-semibold" : "border-border"}`}>
            <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs ${counts[i] ? "bg-amber-100 text-amber-900" : "bg-muted"}`}>{i + 1}</span>
            <span className="min-w-0 truncate">{title}</span>
            {counts[i] ? <span className="ml-auto text-xs text-amber-900" data-testid={`step-count-${i}`}>{counts[i]} eksik</span> : null}
          </button>
        </li>
      ))}
    </ol>
  );
}

function SystemNotice({ items }) {
  if (!items.length) return null;
  const texts = [...new Set(items.map((i) => i.text))];
  return (
    <div className="flex gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800" data-testid="system-blocker" role="status">
      <Wrench className="mt-0.5 h-4 w-4 shrink-0" />
      <div>
        <p className="font-medium">Sistem tarafında bekleyen hazırlık var</p>
        {texts.map((t) => <p key={t}>{t}</p>)}
        <p className="mt-1 text-xs text-slate-600">Bu sizin düzeltebileceğiniz bir eksik değil. Fatura bu hazırlık tamamlanınca içeri alınabilir.</p>
      </div>
    </div>
  );
}

function DocumentProblems({ items }) {
  if (!items.length) return null;
  return (
    <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900" data-testid="document-problems" role="alert">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <div>
        <p className="font-medium">Bu fatura bu akışla içeri alınamaz</p>
        {items.map((i) => <p key={`${i.line}-${i.text}`}>{lineLabel(i.line)}{i.text}</p>)}
        <p className="mt-1 text-xs">Faturanın kendisinden kaynaklanıyor; tedarikçiden düzeltilmiş fatura isteyin veya gelişmiş işlemi kullanın.</p>
      </div>
    </div>
  );
}

function ImportState({ items }) {
  if (!items.length) return null;
  return (
    <div className="flex gap-2 rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-950" data-testid="import-state" role="status">
      <Info className="mt-0.5 h-4 w-4 shrink-0" />
      <div>{items.map((i) => <div key={i.text}><p className="font-medium">{i.text}</p><p className="text-xs">{i.hint}</p></div>)}</div>
    </div>
  );
}

function FixList({ items, onGo, testid = "fix-list" }) {
  if (!items.length) return null;
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950" data-testid={testid}>
      <p className="font-medium">Tamamlamanız gerekenler</p>
      <ul className="mt-1 space-y-1">
        {items.map((i) => (
          <li key={`${i.line}-${i.text}`} className="flex items-center justify-between gap-2">
            <span>{lineLabel(i.line)}{i.text}</span>
            {onGo ? <Button size="sm" variant="outline" className="h-7" onClick={() => onGo(i.step)}>{STEPS[i.step]}</Button> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function CheckStep({ view, decision, issues, createSupplier, onSupplier, onCreateSupplier }) {
  const doc = view.document || {};
  const supplier = view.supplier || {};
  const recon = decision?.document_summary?.reconciliation;
  const candidates = supplier.candidates || [];
  return (
    <div className="space-y-3" data-testid="step-check">
      <div className="grid gap-3 rounded-xl border border-border bg-muted/30 p-4 sm:grid-cols-3">
        <div><p className="text-xs text-muted-foreground">Fatura no</p><p className="font-semibold">{doc.number || "—"}</p></div>
        <div><p className="text-xs text-muted-foreground">Tarih</p><p className="font-semibold">{doc.issue_date ? new Date(`${doc.issue_date}T00:00:00`).toLocaleDateString("tr-TR") : "—"}</p></div>
        <div><p className="text-xs text-muted-foreground">Fatura tutarı</p><p className="font-semibold" data-testid="legal-total">{money(doc.legal_payable, doc.currency)}</p></div>
      </div>
      {recon?.status === "mismatch" || doc.reconciliation === "mismatch" ? (
        <p className="text-sm text-red-900">Satırlardan hesaplanan toplam {money(recon?.computed_payable ?? doc.computed_payable, doc.currency)}; fatura toplamıyla {money(String(recon?.difference ?? doc.difference ?? "").replace(/^-/, ""), doc.currency)} fark var.</p>
      ) : null}

      <div className="rounded-xl border border-border p-4" data-testid="supplier-field">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Tedarikçi</p>
            <p className="truncate font-semibold">{supplier.name || "—"}</p>
            {supplier.tax_number ? <p className="text-xs text-muted-foreground">VKN/TCKN {supplier.tax_number}</p> : null}
          </div>
          {supplier.state === "matched" ? <span className="inline-flex items-center gap-1 text-sm text-emerald-700"><Check className="h-4 w-4" /> Kayıtlı tedarikçiyle eşleşti</span> : null}
        </div>
        {supplier.state !== "matched" && candidates.length ? <div className="mt-3 space-y-1">
          <p className="text-sm">Bu vergi numarasıyla kayıtlı tedarikçilerden birini seçin:</p>
          <div className="flex flex-wrap gap-2" data-testid="supplier-candidates">
            {candidates.map((c) => (
              <Button key={c.id} size="sm" variant={supplier.selected_id === c.id ? "default" : "outline"} aria-pressed={supplier.selected_id === c.id}
                onClick={() => onSupplier(supplier.selected_id === c.id ? null : c.id)}>{c.label || "Adsız tedarikçi"}</Button>
            ))}
          </div>
        </div> : null}
        {supplier.state !== "matched" && !candidates.length && view.decisions?.supplier?.create_new?.available ? <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button size="sm" variant={createSupplier ? "default" : "outline"} aria-pressed={createSupplier} onClick={() => onCreateSupplier(!createSupplier)}>Yeni tedarikçi olarak ekle</Button>
          <span className="rounded bg-sky-100 px-1.5 py-0.5 text-xs text-sky-900">Önizleme</span>
          <span className="text-xs text-muted-foreground">Bugün yalnız önizlenir; tedarikçi kaydı yapılmaz.</span>
        </div> : null}
      </div>

      <ImportState items={issues.filter((i) => i.kind === "state")} />
      <DocumentProblems items={issues.filter((i) => i.kind === "document")} />
      <SystemNotice items={issues.filter((i) => i.kind === "system")} />
      <FixList items={issues.filter((i) => i.kind === "fix" && i.step === 0)} />
    </div>
  );
}

function newIdempotencyKey() {
  const uuid = globalThis.crypto?.randomUUID?.();
  return uuid || `wh-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

const CREATE_ERRORS = {
  WAREHOUSE_NAME_TAKEN: "Bu adla bir depo zaten var. Listeyi yenileyip o depoyu seçebilirsiniz.",
  IDEMPOTENCY_KEY_REUSED: "Bu istek daha önce farklı bir adla gönderilmiş. Formu kapatıp yeniden açın.",
};

// Yerel depo oluşturma: yalnız yönetim açık + liste BAŞARIYLA boş geldiğinde. Oluşturma depoyu SEÇMEZ; "Listeyi yenile" aynı kararlarla önizler.
function WarehouseCreate({ onRefresh }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [key, setKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);
  const start = () => { setOpen(true); setName(""); setError(null); setCreated(null); setKey(newIdempotencyKey()); };
  const submit = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.post("/warehouses", { name }, { headers: { "Idempotency-Key": key } });
      setCreated(result?.name || name.trim());
      setOpen(false);
      setKey(null);                                        // başarıdan sonra yeni istek yeni anahtar alır
    } catch (err) {
      setError({ text: CREATE_ERRORS[err?.code] || "Depo şu an oluşturulamıyor. Biraz sonra yeniden deneyin.", refresh: err?.code === "WAREHOUSE_NAME_TAKEN" });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2" data-testid="warehouse-create">
      {!open ? <Button size="sm" variant="outline" onClick={start}>Depo tanımla</Button> : (
        <div className="flex flex-wrap items-center gap-2">
          <input data-testid="warehouse-create-name" aria-label="Depo adı" className="h-8 rounded-md border border-border px-2 text-sm" maxLength={80}
            value={name} onChange={(e) => setName(e.target.value)} placeholder="Depo adı" />
          <Button size="sm" onClick={submit} disabled={busy || !name.trim()}>Depoyu oluştur</Button>
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Vazgeç</Button>
        </div>
      )}
      {error ? <p className="text-xs text-amber-900" data-testid="warehouse-create-error">{error.text}</p> : null}
      {created ? <p className="text-xs text-emerald-800" data-testid="warehouse-created">{created} oluşturuldu. Seçmek için listeyi yenileyin.</p> : null}
      {created || error?.refresh ? <Button size="sm" variant="outline" onClick={onRefresh}>Listeyi yenile</Button> : null}
    </div>
  );
}

// Depo kararı belge düzeyinde TEK yerde: seçim tüm stok satırlarına uygulanır. Otomatik seçim yalnız ekranda açıkça bildirilir.
function WarehousePicker({ warehouse, onWarehouse, canCreate, onRefresh, autoSelectedId }) {
  const options = warehouse.options || [];
  const autoName = autoSelectedId && warehouse.selected_id === autoSelectedId
    && warehouse.selection_available === true && warehouse.options_complete === true
    && options.length === 1 && !options[0].outside_list && options[0].id === autoSelectedId
    ? options[0].name : null;
  return (
    <div className="space-y-2 rounded-xl border border-border bg-muted/30 p-4 text-sm" data-testid="warehouse-decision">
      <div>
        <p className="font-medium">Hangi depoya girsin?</p>
        <p className="text-xs text-muted-foreground">Seçim bu faturadaki tüm stok satırlarına uygulanır.</p>
      </div>
      {autoName ? <p className="flex items-center gap-1 text-sm text-emerald-800" data-testid="warehouse-auto"><Check className="h-4 w-4" /> {autoName} otomatik seçildi; bu hesaptaki tek depo. Değiştirebilirsiniz.</p> : null}
      {warehouse.selection_available ? <div className="flex flex-wrap gap-2" data-testid="warehouse-options">
        {options.map((o) => (
          <Button key={o.id} size="sm" variant={warehouse.selected_id === o.id ? "default" : "outline"} aria-pressed={warehouse.selected_id === o.id}
            onClick={() => onWarehouse(warehouse.selected_id === o.id ? null : o.id)}>{o.name}</Button>
        ))}
        {!options.length ? <span className="text-muted-foreground">Bu hesapta seçilebilecek depo yok; stok girişini kapatabilirsiniz.</span> : null}
      </div> : <p className="text-muted-foreground">Depo listesi şu an alınamıyor; stok girişini kapatabilirsiniz.</p>}
      {canCreate && warehouse.selection_available === true && !options.length ? <WarehouseCreate onRefresh={onRefresh} /> : null}
      {warehouse.selection_available && warehouse.options_complete === false ? <p className="text-xs text-muted-foreground">Yalnız ilk 50 depo gösteriliyor.</p> : null}
      {warehouse.default_warehouse && warehouse.selected_id !== warehouse.default_warehouse.id ? (
        <p className="text-xs">Varsayılan deponuz {warehouse.default_warehouse.name}; kendiliğinden seçilmez. <button type="button" className="underline" onClick={() => onWarehouse(warehouse.default_warehouse.id)}>Bu depoyu seç</button></p>
      ) : null}
    </div>
  );
}

function Fact({ label, children }) {
  return <div className="min-w-0"><dt className="text-xs text-muted-foreground">{label}</dt><dd className="font-medium">{children}</dd></div>;
}

const PRODUCT_STATE_TEXT = { matched: "Kayıtlı ürünle eşleşti", create: "Yeni ürün önizlemesi", unmatched: "Kayıtlı ürün bulunamadı", ambiguous: "Birden fazla kayıtlı ürün uyuyor" };

// Satır: önce düzenli özet (sunucu tutarları, istemcide yeniden hesap YOK). Karar kontrolleri yalnız çözülmesi gerekiyorsa açık; aksi hâlde "Değiştir".

function LineCard({ line, currency, issues, stockOn, onKind, onProduct, onRemember }) {
  const product = line.product || {};
  const candidates = product.candidates || [];
  const fixes = issues.filter((i) => i.kind === "fix");
  const problems = issues.filter((i) => i.kind === "document");
  const notices = issues.filter((i) => i.kind === "notice");
  const needsDecision = fixes.length > 0 || !line.kind || (line.kind === "product" && candidates.length > 0 && !product.selected_id)
    || !!product.remember_selected;                                                       // açık önizleme seçimi görünür kalır
  const [expanded, setExpanded] = useState(false);
  const cardRef = useRef(null);
  const focusControls = useRef(false);
  const busy = useContext(BusyContext);
  // Kontroller bir kez açıldıysa açık kalır: çözülünce kapanıp odağı düşürmez.
  useEffect(() => { if (needsDecision) setExpanded(true); }, [needsDecision]);
  useEffect(() => {
    if (expanded && focusControls.current) { focusControls.current = false; cardRef.current?.querySelector('[role="radio"][tabindex="0"]')?.focus(); }
  }, [expanded]);
  const showControls = needsDecision || expanded;
  const vat = money(line.vat_amount_provider ?? line.vat_amount_computed, currency);
  const kindText = line.kind === "service" ? "Hizmet · yalnız fatura satırı" : line.kind === "product" ? `Ürün${PRODUCT_STATE_TEXT[product.state] ? ` · ${PRODUCT_STATE_TEXT[product.state]}` : ""}` : null;
  return (
    <div className={`rounded-xl border p-4 ${fixes.length ? "border-amber-300" : problems.length ? "border-red-300" : "border-border"}`} data-testid={`line-${line.index}`} ref={cardRef}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="min-w-0 font-medium"><span className="mr-2 text-xs text-muted-foreground">Satır {line.index + 1}</span>{line.description || "Açıklama yok"}</p>
        {stockOn && line.kind === "product" && line.stock?.eligible ? <span className="rounded bg-muted px-1.5 py-0.5 text-xs">Stoğa girer</span> : null}
      </div>
      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-5" data-testid={`line-facts-${line.index}`}>
        <Fact label="Miktar">{quantity(line.quantity, line.unit)}</Fact>
        <Fact label="Birim fiyat">{money(line.unit_price, currency)}</Fact>
        <Fact label="İskonto">{line.discount ? money(line.discount, currency) : "—"}</Fact>
        <Fact label="KDV">%{line.vat_rate ?? "—"} · {vat}</Fact>
        <Fact label="Toplam">{money(line.line_extension_provider ?? line.net_computed, currency)}</Fact>
      </dl>
      {!showControls && kindText ? <div className="mt-2 flex items-center gap-2 text-sm">
        <span className="inline-flex items-center gap-1 text-emerald-800"><Check className="h-4 w-4" /> {kindText}</span>
        <button type="button" className="text-xs underline" onClick={() => { focusControls.current = true; setExpanded(true); }}>Değiştir</button>
      </div> : null}
      {showControls ? <>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Segmented name={`Satır ${line.index + 1} türü`} value={line.kind} options={[["product", "Ürün"], ["service", "Hizmet"]]} onChange={(v) => { setExpanded(true); onKind(line.index, v); }} />
          {line.kind === "service" ? <span className="text-xs text-muted-foreground">Yalnız fatura satırı olarak kalır; stoğa girmez.</span> : null}
        </div>
        {line.kind === "product" ? <div className="mt-3 space-y-2" data-testid={`product-choice-${line.index}`}>
          {product.state === "matched" && !candidates.length ? <p className="inline-flex items-center gap-1 text-sm text-emerald-700"><Check className="h-4 w-4" /> Kayıtlı ürünle eşleşti</p> : null}
          {candidates.length ? <div>
            <p className="text-sm">Kayıtlı ürünlerden seçin:</p>
            <div className="mt-1 flex flex-wrap gap-2">
              {candidates.map((c) => (
                <Button key={c.id} size="sm" className="h-auto max-w-full whitespace-normal text-left" aria-disabled={busy} variant={product.selected_id === c.id ? "default" : "outline"} aria-pressed={product.selected_id === c.id}
                  onClick={() => onProduct(line.index, product.selected_id === c.id ? null : c.id)}>{c.label || "Adsız ürün"}{c.sku ? ` · ${c.sku}` : ""}</Button>
              ))}
            </div>
          </div> : null}
          {/* Katalogda aday varken yeni ürün önerilmez (sunucu CATALOG_DUPLICATE ile reddeder). */}
          {product.state !== "matched" && !product.selected_id && !candidates.length ? <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1" checked={!!product.remember_selected} onChange={(e) => onRemember(line.index, e.target.checked)} data-testid={`remember-${line.index}`} />
            <span>Yeni ürün olarak önizle
              <span className="block text-xs text-muted-foreground">Bu sürümde ürün kataloğa eklenmez; yalnız sonucun nasıl görüneceğini gösterir.</span></span>
          </label> : null}
        </div> : null}
      </> : null}
      {fixes.map((i) => <p key={i.text} className="mt-2 text-sm text-amber-900" role="note">→ {i.text}</p>)}
      {problems.map((i) => <p key={i.text} className="mt-2 text-sm text-red-900" role="note">{i.text}</p>)}
      {notices.map((i) => <p key={i.text} className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground"><Info className="h-3 w-3" /> {i.text}</p>)}
    </div>
  );
}

function LinesStep({ view, issues, stockOn, onStock, warehouseManagement, onWarehouse, onRefresh, autoSelectedId, ...handlers }) {
  const decisions = view.decisions || {};
  const byLine = (index) => issues.filter((i) => i.line === index);
  const docFixes = issues.filter((i) => i.kind === "fix" && i.step === 1 && i.line == null);
  const hasStockLine = (view.lines || []).some((l) => l.kind === "product" && l.stock?.eligible);
  const showWarehouse = stockOn && hasStockLine && decisions.warehouse?.required;
  return (
    <div className="space-y-3" data-testid="step-lines">
      {decisions.stock_action?.available ? <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border p-4" data-testid="stock-decision">
        <div><p className="font-medium">Ürünler stoğa girsin mi?</p><p className="text-xs text-muted-foreground">Kapalıyken depo sorulmaz ve stok hareketi oluşmaz.</p></div>
        <Segmented name="Stok girişi" value={stockOn ? "on" : "off"} options={[["off", "Stoğa girmesin"], ["on", "Stoğa girsin"]]} onChange={(v) => onStock(v === "on")} />
      </div> : null}
      {showWarehouse ? <WarehousePicker warehouse={decisions.warehouse} onWarehouse={onWarehouse} canCreate={warehouseManagement} onRefresh={onRefresh} autoSelectedId={autoSelectedId} /> : null}
      {(view.lines || []).map((line) => (
        <LineCard key={line.index} line={line} currency={view.document?.currency} issues={byLine(line.index)} stockOn={stockOn} {...handlers} />
      ))}
      {docFixes.length ? <FixList items={docFixes} testid="fix-list-lines" /> : null}
    </div>
  );
}

function SummaryRow({ title, value, detail, testid }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-2 border-b border-border py-3 last:border-0" data-testid={testid}>
      <div className="min-w-0"><p className="font-medium">{title}</p>{detail ? <p className="text-xs text-muted-foreground">{detail}</p> : null}</div>
      <p className="text-right font-semibold">{value}</p>
    </div>
  );
}

function ReviewStep({ view, decision, issues, stockOn, onGo }) {
  const doc = view.document || {};
  const supplier = view.supplier || {};
  const effects = new Set((decision?.effect_summary || []).map((e) => e.key));
  // Hazır olmak SUNUCU kararıdır: FAST_READY + business_ready=true ve gösterilecek engel yok. İstemci listesinin boş olması hazır demek değildir.
  const ready = decision?.status === "FAST_READY" && decision?.business_ready === true && !issues.some((i) => i.kind !== "notice");
  const pending = "Eksikler tamamlanınca";
  const warehouse = view.decisions?.warehouse || {};
  const whName = (warehouse.options || []).find((o) => o.id === warehouse.selected_id)?.name;
  const stockLines = (view.lines || []).filter((l) => l.kind === "product" && l.stock?.eligible);
  const remembered = (view.lines || []).filter((l) => l.kind === "product" && l.product?.remember_selected);
  const external = effects.has("EXPENSE_NOT_RECORDED_EXTERNAL");
  const total = money(doc.legal_payable, doc.currency);
  // Yazım bu sürümde kapalı: etkiler koşullu ("İçeri alındığında") anlatılır, olmuş/olacak gibi vaat edilmez.
  const invoiceState = external ? "Gider kaydını bağlı muhasebe programınız yönetir." : effects.has("EXPENSE_INVOICE_CREATED") ? "İçeri alındığında Alış & Gider Faturaları'na gider faturası olarak eklenir." : "Eksikler tamamlanınca belirlenir.";
  const debtDetail = effects.has("SUPPLIER_DEBT_NOT_RECORDED_EXTERNAL") ? "Borcu bağlı muhasebe programınız yönetir; ObiHub'da cari borç oluşmaz."
    : effects.has("SUPPLIER_DEBT_RECORDED") ? `İçeri alındığında ${supplier.name || "tedarikçi"} carisine borç kaydedilir.` : "Eksikler tamamlanınca belirlenir.";
  const stock = !stockOn || !stockLines.length ? null : !whName
    ? { value: "Depo seçilmedi", detail: "Stoğa girecek ürün satırları için depo seçin." }
    : { value: `${stockLines.length} satır → ${whName}`, detail: stockLines.map((l) => `${l.description || `Satır ${l.index + 1}`}: ${quantity(l.quantity, l.unit)}`).join(" · ") };
  return (
    <div className="space-y-3" data-testid="step-review">
      <div className="rounded-xl border border-border px-4" data-testid="result-summary">
        <SummaryRow testid="sum-invoice" title="Alış faturası" value={total} detail={`${doc.number ? `${doc.number} · ` : ""}${supplier.name || "Tedarikçi"}. ${invoiceState}`} />
        <SummaryRow testid="sum-debt" title="Cari borç" value={effects.has("SUPPLIER_DEBT_RECORDED") ? total : effects.has("SUPPLIER_DEBT_NOT_RECORDED_EXTERNAL") ? "Muhasebe programınızda" : pending}
          detail={`${debtDetail}${effects.has("NEW_SUPPLIER_CREATED") ? " Yeni tedarikçi yalnız önizlemedir." : ""}`} />
        {stock ? <SummaryRow testid="sum-stock" title="Stok etkisi" value={stock.value} detail={stock.detail} /> : null}
        <SummaryRow testid="sum-payment" title="Ödeme durumu" value="Ödenecek" detail="Bu işlemde ödeme yapılmaz. Ödemeyi sonra hesap, tarih ve tutar seçerek eklersiniz." />
      </div>
      {remembered.length ? <p className="text-xs text-muted-foreground" data-testid="sum-remember">
        Yeni ürün önizlemesi ({remembered.map((l) => l.description || `Satır ${l.index + 1}`).join(", ")}): bu sürümde kataloğa eklenmez.</p> : null}
      <ImportState items={issues.filter((i) => i.kind === "state")} />
      <DocumentProblems items={issues.filter((i) => i.kind === "document")} />
      <SystemNotice items={issues.filter((i) => i.kind === "system")} />
      <FixList items={issues.filter((i) => i.kind === "fix")} onGo={onGo} testid="fix-list-review" />
      {ready ? <p className="flex items-center gap-2 text-sm text-emerald-800" data-testid="review-ready"><Check className="h-4 w-4" /> Bilgiler tamam.</p>
        : <p className="text-sm text-muted-foreground" data-testid="review-not-ready">Fatura henüz hazır değil; içeri alma için sunucu onayı bekleniyor.</p>}
    </div>
  );
}

export default function QuickImportWizard({ open, onClose, invoice, warehouseManagement = false }) {
  const [step, setStep] = useState(0);
  const prevStep = useRef(0);
  const titleRef = useRef(null);
  const q = useQuickImportPreview({ open, invoice, onReset: () => setStep(0), onRetried: () => titleRef.current?.focus() });
  const { mode, decision, loading, failed, autoSelectedId, previewError, announce, view, issues, retry, reload,
    chooseKind, chooseStock, chooseProduct, chooseRemember, chooseSupplier, chooseCreateSupplier, chooseWarehouse } = q;
  const createSupplier = q.createSupplier;
  const counts = useMemo(() => [0, 1, 2].map((s) => (s === 2 ? 0 : issues.filter((i) => i.kind === "fix" && i.step === s).length)), [issues]);


  useEffect(() => {
    if (step !== prevStep.current) titleRef.current?.focus();
    prevStep.current = step;
  }, [step]);

  const stockOn = mode === "expense_and_stock";

  const stale = previewError !== null;
  const body = !view ? null : step === 0
    ? <CheckStep view={view} decision={decision} issues={issues} createSupplier={createSupplier} onSupplier={chooseSupplier} onCreateSupplier={chooseCreateSupplier} />
    : step === 1
      ? <LinesStep view={view} issues={issues} stockOn={stockOn} onStock={chooseStock} onKind={chooseKind} onProduct={chooseProduct} onRemember={chooseRemember} onWarehouse={chooseWarehouse}
          warehouseManagement={warehouseManagement === true} onRefresh={() => reload({})} autoSelectedId={autoSelectedId} />
      : <ReviewStep view={view} decision={stale || loading ? { ...decision, status: "STALE", business_ready: false } : decision} issues={issues} stockOn={stockOn} onGo={setStep} />;

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value) onClose?.(); }}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="quick-import-wizard">
        <DialogHeader>
          <DialogTitle>Faturayı içeri al</DialogTitle>
          <DialogDescription>{invoice?.sender_name || invoice?.supplier_name || "Gelen fatura"} · {invoice?.invoice_no || "e-Fatura"}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <StepHeader step={step} onStep={setStep} counts={counts} />
          <h3 ref={titleRef} tabIndex={-1} className="sr-only" data-testid="step-title">{`Adım ${step + 1} / 3: ${STEPS[step]}`}</h3>
          <p className="sr-only" role="status" aria-live="polite" data-testid="wizard-live">{announce}</p>
          {loading && !view ? <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Fatura hazırlanıyor</div> : null}
          {!loading && !view && (failed || decision?.document_view_error || decision) ? (
            <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800" role="alert" data-testid="view-unavailable">
              <p>Fatura satırları şu an güvenle gösterilemiyor. Biraz sonra yeniden deneyin; sorun sürerse gelişmiş işlemi kullanın.</p>
              <Button size="sm" variant="outline" onClick={retry}>Yeniden dene</Button>
            </div>
          ) : null}
          {stale && view ? (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950" role="alert" data-testid="preview-error">
              <p>{PREVIEW_ERROR_TEXT[previewError] || PREVIEW_ERROR_TEXT.server} Son değişikliğiniz uygulanmadı; ekranda son başarılı önizleme ve ona ait seçimler var.</p>
              <Button size="sm" variant="outline" onClick={() => { if (!loading) retry(); }} aria-disabled={loading}>Yeniden dene</Button>
            </div>
          ) : null}
          {loading && view ? <p className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="preview-updating"><Loader2 className="h-3 w-3 animate-spin" /> Önizleme güncelleniyor…</p> : null}
          {view && !decision?.document_view_error ? <BusyContext.Provider value={loading}><div className={loading ? "pointer-events-none opacity-60" : ""} aria-busy={loading}>{body}</div></BusyContext.Provider> : null}
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
            <Button variant="ghost" onClick={onClose}>Vazgeç</Button>
            <div className="flex flex-wrap items-center justify-end gap-2">
              {step > 0 ? <Button variant="outline" onClick={() => setStep(step - 1)}>Geri</Button> : null}
              {step < 2 ? <Button onClick={() => setStep(step + 1)} disabled={!view}>Devam</Button> : (
                <>
                  <span className="text-xs text-muted-foreground" data-testid="readonly-note">Önizleme sürümü: kayıt bu aşamada açık değil.</span>
                  <Button disabled aria-disabled="true">İçeri al</Button>
                </>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
